import { useState, useEffect, useCallback, useRef } from 'react';
import Api from 'entities/helpers/api';

/**
 * Fetches portfolio teams (volunteer pools type=2) and provides
 * a function to get stages for a selected team.
 */
const usePortfolioTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const stagesCacheRef = useRef({});
  const teamsRef = useRef([]);

  useEffect(() => {
    try {
      const request = Api.VOLUNTEER.POOLS.get({ params: { pool_type: 'TEAM', limit: 200 } });
      Promise.resolve(request)
        .then((resp) => {
          const items = (Array.isArray(resp) ? resp : resp?.items || resp?.pools || []).map((pool) => ({
            id: String(pool.id),
            name: pool.name || `Team ${pool.id}`,
            stageGroupId: pool.stage_group_id || pool.stageGroupId || null,
          }));
          setTeams(items);
          teamsRef.current = items;
        })
        .catch(() => setTeams([]))
        .finally(() => setLoading(false));
    } catch {
      setTeams([]);
      setLoading(false);
    }
  }, []);

  /** Fetch stages for a given team. Caches results per team ID. */
  const getStages = useCallback((teamId) => {
    if (!teamId) return Promise.resolve([]);
    if (stagesCacheRef.current[teamId]) return Promise.resolve(stagesCacheRef.current[teamId]);

    const team = teamsRef.current.find((t) => t.id === String(teamId));
    if (!team?.stageGroupId) return Promise.resolve([]);

    return Promise.resolve(Api.VOLUNTEER.STAGES.get({ urlExtend: `/${team.stageGroupId}` }))
      .then((resp) => {
        const stages = (resp?.stages || [])
          .filter((s) => s.active !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((s) => ({
            id: String(s.id),
            name: s.stage || s.name || `Stage ${s.id}`,
          }));
        stagesCacheRef.current[teamId] = stages;
        return stages;
      })
      .catch(() => []);
  }, []);

  return { teams, getStages, stagesMap: stagesCacheRef.current, loading };
};

export default usePortfolioTeams;
